"use client";
import Image from "next/image";
import Link from "next/link";
import ExpandableBlock from "@/components/ContentBlock/variants/ExpandableBlock";
import { ProcessedCourse } from "@/common/course/types";
import { PartialPartner } from "@/common/partner/types";
import { COURSE_RUN_AVAILABILITY_ARCHIVED } from "@/lib/course/utils/constants";
import getCoursePaymentOption from "@/lib/course/utils/getCoursePaymentOption";
import CourseSnapshot from "./CourseSnapshot";
import GetSmarterLogo from "./GetSmarterLogo";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import { ExecEdATFTestResult } from "@/monarch/rules/execEdATF/types";

interface Props {
  course: ProcessedCourse;
}

const CourseInfo = ({ course }: Props) => {
  function getOrganizationLogo(partner: PartialPartner) {
    const logo = (
      <Image
        width={150}
        height={75}
        className="max-w-[165px] md:max-w-full"
        src={partner.logoUrl}
        alt={partner.name || "Organization Logo"}
        key={partner.key}
      />
    );
    if (!course.partnerLogoOverride && course.partnerNameOverride) {
      return (
        <Link
          className="mr-2 mb-0"
          href={`/school/${partner.slug.toLocaleLowerCase()}`}
          key={partner.key}
        >
          {logo}
        </Link>
      );
    }
    return logo;
  }

  const title = `${course.partners[0]?.name}: ${course.productName}`;
  const subheader: string = course.productShortDescription.replace(
    /<[^>]+>/g,
    "",
  );
  const { externalIdentifier, organicUrl, productStatus } =
    course.additionalMetadata.at(0) || {};
  const cartUrl = `https://getsmarter.com/titan/api/v1/cart/add_item/${externalIdentifier}`;
  const isArchived = productStatus === COURSE_RUN_AVAILABILITY_ARCHIVED;
  const paymentOption = getCoursePaymentOption(course);
  const courseDisclaimer =
    course.additionalMetadata.at(0)?.courseTermOverride?.toLowerCase() ||
    "course";

  return (
    <>
      <div className="mb-6 md:mt-8 flex flex-row justify-center md:justify-start w-full">
        {course.partners &&
          course.partners.map((partner) => getOrganizationLogo(partner))}
      </div>
      <h1 className="text-3xl font-bold mt-2 mb-3 lg:mb-4 p-0">{title}</h1>
      <ExpandableBlock maxHeight={60} heading="" blur centered={false}>
        <p className="" style={{ wordBreak: "keep-all" }}>
          {subheader}
        </p>
      </ExpandableBlock>
      <MonarchDataConsumer<ExecEdATFTestResult>
        experimentKey="useExecEdATF"
        render={(testResult) => {
          const { data } = testResult;
          return (
            <CourseSnapshot
              course={course}
              paymentOption={paymentOption}
              className="hidden md:block mt-4"
              hasDateColumn
              isArchivedExternally={isArchived}
              certificateFactoid={data?.certificateFactoid}
            />
          );
        }}
      />
      <div className="hidden md:block mt-auto">
        <div className="w-full border-b border-primary-200" />
        <GetSmarterLogo courseDisclaimer={courseDisclaimer} />
      </div>
    </>
  );
};

export default CourseInfo;
